/**
 * PlateCalculator — Barbell Plate Breakdown Overlay
 *
 * Assumes a standard 45 lb Olympic barbell.
 * Calculates exact plates per side to reach the target weight.
 * Visual display: horizontal plate stack with color-coded plates.
 */

import { useMemo } from 'react';

// Standard Olympic plate weights (lbs), heaviest first
const PLATE_SIZES = [45, 35, 25, 10, 5, 2.5] as const;
type PlateWeight = (typeof PLATE_SIZES)[number];

// Color coding matching real bumper/iron plates
const PLATE_COLORS: Record<PlateWeight, string> = {
  45:  'bg-[#1a1a2e] border-[#4444aa]',    // dark blue
  35:  'bg-[#1a2e1a] border-[#44aa44]',    // green
  25:  'bg-[#2e1a1a] border-[#aa4444]',    // red
  10:  'bg-[#2e2e1a] border-[#aaaa44]',    // yellow
  5:   'bg-[#2e2e2e] border-[#888888]',    // grey
  2.5: 'bg-[#1a2e2e] border-[#44aaaa]',   // teal
};

const PLATE_TEXT: Record<PlateWeight, string> = {
  45:  '45', 35: '35', 25: '25', 10: '10', 5: '5', 2.5: '2.5',
};

const BARBELL_WEIGHT = 45;

interface PlateBreakdown {
  platesPerSide: { weight: PlateWeight; count: number }[];
  totalWeight: number;
  isAchievable: boolean;
  remainder: number;
}

function calcPlates(targetWeight: number): PlateBreakdown {
  const weightPerSide = (targetWeight - BARBELL_WEIGHT) / 2;

  if (weightPerSide < 0) {
    return { platesPerSide: [], totalWeight: BARBELL_WEIGHT, isAchievable: false, remainder: 0 };
  }

  let remaining = weightPerSide;
  const platesPerSide: { weight: PlateWeight; count: number }[] = [];

  for (const plate of PLATE_SIZES) {
    if (remaining <= 0) break;
    const count = Math.floor(remaining / plate);
    if (count > 0) {
      platesPerSide.push({ weight: plate, count });
      remaining -= count * plate;
    }
  }

  // Round remainder to avoid floating-point noise
  const remainder = Math.round(remaining * 10) / 10;
  const actualTotal = targetWeight - remainder * 2;

  return {
    platesPerSide,
    totalWeight: Math.round(actualTotal * 10) / 10,
    isAchievable: remainder < 0.01,
    remainder,
  };
}

interface PlateCalculatorProps {
  targetWeight: number;
  weightUnit?: 'lbs' | 'kg';
  onClose: () => void;
}

export default function PlateCalculator({ targetWeight, weightUnit = 'lbs', onClose }: PlateCalculatorProps) {
  // Convert kg → lbs for plate math, then display in lbs
  const weightInLbs = weightUnit === 'kg' ? targetWeight * 2.2046 : targetWeight;
  const breakdown = useMemo(() => calcPlates(weightInLbs), [weightInLbs]);

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center pb-safe">
      {/* Backdrop */}
      <button
        className="absolute inset-0 bg-black/60"
        onClick={onClose}
        aria-label="Close plate calculator"
      />

      {/* Panel */}
      <div className="relative w-full max-w-sm bg-surface-100 rounded-t-3xl
                      px-6 pt-4 pb-8 shadow-card-lg animate-slide-up">

        {/* Handle */}
        <div className="w-10 h-1 bg-surface-400 rounded-full mx-auto mb-5" />

        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold text-ink">Plate Calculator</h3>
          <button className="btn-ghost p-1" onClick={onClose}>✕</button>
        </div>

        {/* Target weight display */}
        <div className="card text-center mb-5">
          <p className="text-xs text-ink-muted uppercase tracking-widest mb-1">Target Weight</p>
          <p className="text-4xl font-bold font-mono text-ink">{targetWeight}</p>
          <p className="text-sm text-ink-muted">{weightUnit}</p>
          {weightUnit === 'kg' && (
            <p className="text-xs text-ink-faint mt-1">
              = {Math.round(weightInLbs)} lbs · plates shown in lbs
            </p>
          )}
        </div>

        {/* Barbell visual */}
        <div className="flex items-center justify-center gap-1 mb-5 overflow-x-auto no-scrollbar py-2">
          {/* Left sleeve plates (reversed) */}
          {[...breakdown.platesPerSide].reverse().flatMap(({ weight, count }) =>
            Array.from({ length: count }, (_, i) => (
              <div
                key={`L-${weight}-${i}`}
                className={`flex-shrink-0 border-2 rounded-sm flex items-center justify-center
                            text-xs font-bold text-white
                            ${PLATE_COLORS[weight]}`}
                style={{ width: weight >= 25 ? 20 : weight >= 10 ? 16 : 12,
                         height: weight >= 25 ? 64 : weight >= 10 ? 56 : 48 }}
              >
                {weight >= 10 ? PLATE_TEXT[weight] : ''}
              </div>
            ))
          )}

          {/* Barbell collar */}
          <div className="flex-shrink-0 w-3 h-10 bg-surface-500 rounded-sm" />
          {/* Bar */}
          <div className="flex-shrink-0 w-16 h-3 bg-surface-400 rounded-full" />
          {/* Collar */}
          <div className="flex-shrink-0 w-3 h-10 bg-surface-500 rounded-sm" />

          {/* Right sleeve plates */}
          {breakdown.platesPerSide.flatMap(({ weight, count }) =>
            Array.from({ length: count }, (_, i) => (
              <div
                key={`R-${weight}-${i}`}
                className={`flex-shrink-0 border-2 rounded-sm flex items-center justify-center
                            text-xs font-bold text-white
                            ${PLATE_COLORS[weight]}`}
                style={{ width: weight >= 25 ? 20 : weight >= 10 ? 16 : 12,
                         height: weight >= 25 ? 64 : weight >= 10 ? 56 : 48 }}
              >
                {weight >= 10 ? PLATE_TEXT[weight] : ''}
              </div>
            ))
          )}
        </div>

        {/* Plate list */}
        <div className="space-y-2 mb-4">
          <p className="section-header">Per Side</p>
          {breakdown.platesPerSide.length === 0 ? (
            <p className="text-ink-muted text-sm text-center py-2">Bar only (45 lbs)</p>
          ) : (
            breakdown.platesPerSide.map(({ weight, count }) => (
              <div key={weight} className="flex items-center justify-between
                                          bg-surface-200 rounded-xl px-4 py-2.5">
                <div className="flex items-center gap-3">
                  <div className={`w-4 h-8 border-2 rounded-sm ${PLATE_COLORS[weight]}`} />
                  <span className="font-semibold text-ink">{weight} lb plate</span>
                </div>
                <span className="text-ink-muted font-mono">×{count}</span>
              </div>
            ))
          )}
        </div>

        {/* Total line */}
        <div className="flex justify-between items-center bg-surface-300 rounded-xl px-4 py-3">
          <span className="text-sm text-ink-muted">Bar (45 lb) + plates</span>
          <span className={`font-bold text-lg font-mono
            ${breakdown.isAchievable ? 'text-success' : 'text-warning'}`}>
            {breakdown.totalWeight} lbs
          </span>
        </div>

        {!breakdown.isAchievable && (
          <p className="text-warning text-xs text-center mt-2">
            Nearest achievable: {breakdown.totalWeight} lbs
            ({breakdown.remainder > 0 ? `–${breakdown.remainder * 2}` : `+${Math.abs(breakdown.remainder) * 2}`} lbs)
          </p>
        )}
      </div>
    </div>
  );
}
